import React from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiCheckCircle, FiAlertTriangle, FiMail, FiPhone } from 'react-icons/fi';

const TermsOfService = () => {
    const sections = [
        {
            title: 'Acceptance of Terms',
            content: [
                'By accessing or using Primeforce Services website and services, you agree to be bound by these Terms of Service',
                'If you do not agree to these terms, please do not use our services',
                'We reserve the right to modify these terms at any time, and such modifications will be effective immediately upon posting',
                'Your continued use of our services constitutes acceptance of any modified terms'
            ]
        },
        {
            title: 'Services Description',
            content: [
                'Primeforce Services provides professional cleaning and maintenance services for commercial, residential, and industrial sectors',
                'Service scope, pricing, and schedules are determined based on individual service agreements',
                'We strive to provide services as described, but reserve the right to modify service offerings',
                'All services are subject to availability and may vary by location'
            ]
        },
        {
            title: 'Service Agreements & Scheduling',
            content: [
                'All service engagements require a formal service agreement or contract',
                'Scheduling of services is subject to availability and mutual agreement',
                'Cancellation or rescheduling must be communicated at least 24 hours in advance',
                'Late cancellations may be subject to cancellation fees as outlined in your service agreement'
            ]
        },
        {
            title: 'Payment Terms',
            content: [
                'Payment terms are specified in individual service contracts or quotes',
                'Accepted payment methods will be communicated upon service agreement',
                'Late payments may incur additional charges as specified in your contract',
                'Disputed charges must be reported within 14 days of invoice receipt'
            ]
        },
        {
            title: 'Client Responsibilities',
            content: [
                'Provide accurate information when requesting services or submitting inquiries',
                'Ensure safe and accessible working conditions for our staff',
                'Notify us of any hazardous materials or conditions at the service location',
                'Secure valuables and personal items before service appointments',
                'Provide necessary access to water, electricity, and other utilities as required'
            ]
        },
        {
            title: 'Liability & Insurance',
            content: [
                'Primeforce Services maintains comprehensive insurance coverage for all operations',
                'Our liability is limited to the value of services provided as per the service agreement',
                'We are not responsible for pre-existing damage or conditions at service locations',
                'Claims for damage must be reported within 48 hours of service completion',
                'Force majeure events exempt us from liability for service delays or interruptions'
            ]
        },
        {
            title: 'Quality Guarantee',
            content: [
                'We are committed to delivering high-quality services that meet industry standards',
                'If you are not satisfied with our service, please contact us within 24 hours',
                'We will address legitimate concerns and, if necessary, provide remedial services',
                'Our quality guarantee does not cover issues arising from client negligence or pre-existing conditions'
            ]
        },
        {
            title: 'Intellectual Property',
            content: [
                'All content on this website is the property of Primeforce Services',
                'You may not reproduce, distribute, or create derivative works without written permission',
                'Our trademarks, logos, and service marks may not be used without prior authorization',
                'Unauthorized use of our intellectual property may result in legal action'
            ]
        },
        {
            title: 'Website Use',
            content: [
                'You agree to use our website only for lawful purposes',
                'You may not attempt to gain unauthorized access to our systems',
                'We are not responsible for content on third-party websites linked from our site',
                'Website availability is not guaranteed and may be interrupted for maintenance'
            ]
        },
        {
            title: 'Termination',
            content: [
                'Either party may terminate the service agreement as per contractual terms',
                'We reserve the right to refuse service to anyone at our discretion',
                'Termination does not affect any accrued rights or obligations',
                'Upon termination, all outstanding payments become immediately due'
            ]
        }
    ];

    return (
        <div className="w-full">
            {/* Hero Banner */}
            <section className="relative py-24 md:py-32">
                <div className="absolute inset-0">
                    <div className="w-full h-full bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800"></div>
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-20 right-20 w-64 h-64 bg-accent-500 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                    </div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        className="w-20 h-20 mx-auto mb-6 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center"
                    >
                        <FiFileText className="text-accent-400" size={40} />
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
                    >
                        Terms of <span className="text-accent-400">Service</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-xl text-white/80 max-w-2xl mx-auto"
                    >
                        Please read these terms carefully before using our services.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="text-white/60 mt-6 text-sm"
                    >
                        Last updated: January 2026
                    </motion.p>
                </div>
            </section>

            {/* Terms Content */}
            <section className="section-padding bg-white">
                <div className="container-custom max-w-4xl">
                    {/* Introduction */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mb-12"
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <FiAlertTriangle className="text-amber-600" size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800 mb-3">Important Notice</h2>
                                <p className="text-gray-600 leading-relaxed">
                                    These Terms of Service govern your use of the Primeforce Services website and all cleaning
                                    and maintenance services provided by us. By engaging our services or using our website,
                                    you acknowledge that you have read, understood, and agree to be bound by these terms.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Terms Sections */}
                    <div className="space-y-10">
                        {sections.map((section, index) => (
                            <motion.div
                                key={section.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.03 }}
                                className="border-b border-gray-100 pb-10 last:border-0"
                            >
                                <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                                    <span className="w-8 h-8 bg-primary-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">
                                        {index + 1}
                                    </span>
                                    {section.title}
                                </h3>
                                <ul className="space-y-3">
                                    {section.content.map((item, itemIndex) => (
                                        <li key={itemIndex} className="flex items-start gap-3 text-gray-600">
                                            <FiCheckCircle className="text-accent-500 mt-1 flex-shrink-0" size={16} />
                                            <span className="leading-relaxed">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Governing Law */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-12 bg-gray-50 rounded-2xl p-8"
                    >
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Governing Law</h3>
                        <p className="text-gray-600 leading-relaxed">
                            These Terms of Service shall be governed by and construed in accordance with the laws of the
                            jurisdiction in which Primeforce Services operates. Any disputes arising from these terms
                            shall be subject to the exclusive jurisdiction of the courts in that jurisdiction.
                        </p>
                    </motion.div>

                    {/* Contact Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 text-white"
                    >
                        <h3 className="text-2xl font-bold mb-4">Questions About Our Terms?</h3>
                        <p className="text-white/80 mb-6">
                            If you have any questions about these Terms of Service, please contact us.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a
                                href="mailto:info@primeforceservices.com"
                                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-colors"
                            >
                                <FiMail size={20} />
                                <span>info@primeforceservices.com</span>
                            </a>
                            <a
                                href="tel:+1234567890"
                                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-colors"
                            >
                                <FiPhone size={20} />
                                <span>+123 456 7890</span>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default TermsOfService;
